import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  AutoComplete,
  Button,
  Col,
  Divider,
  Form,
  Grid,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  AlertCircle,
  Calculator,
  CheckCircle,
  CircleDollarSign,
  Database,
  Pencil,
  History,
  Package,
  Percent,
  RefreshCw,
  Save,
  Search,
  Target,
  TrendingUp,
  Truck,
  Trash2,
  Wallet,
} from "lucide-react";
import http, { isSubscriptionRedirectError } from "../../../api/http";
import {
  clearPendingPublicCalc,
  readPendingPublicCalc,
  writePendingPublicCalc,
} from "./pendingPublicCalc";
import {
  SCHEMES,
  TAX_SYSTEMS,
  createFormDefaults,
  formValuesToInput,
  getDefaultBuyoutPercent,
  inputToFormValues,
  mergeCategoryLists,
  normalizeCategoryText,
  parseInputNumber,
  parseNumber,
} from "./unitForm";
import "./unitPlanningCalculator.css";

const { Text, Title } = Typography;

function getErrorNotice(error, fallback) {
  if (isSubscriptionRedirectError(error)) {
    return null;
  }

  return {
    type: "error",
    text: error?.response?.data?.message || fallback,
  };
}

const COST_LABELS = {
  cogs: "Себестоимость",
  commission: "Комиссия",
  logistics: "Логистика",
  acceptance: "Приёмка",
  storage: "Хранение",
  processing: "Обработка",
  acquiring: "Эквайринг",
  advertising: "Реклама",
  ownDelivery: "Своя доставка",
  ownReturn: "Свой возврат",
  taxUSN: "УСН",
  vatPayable: "НДС к уплате",
  otherCosts: "Прочие",
  total: "Всего затрат",
};

const LOGISTICS_KIND_LABELS = {
  forward: "Прямая логистика",
  reverse: "Обратная логистика",
  last_mile: "Последняя миля",
  acceptance: "Приемка",
  storage: "Хранение",
  processing: "Обработка",
  own_delivery: "Своя доставка",
  own_return: "Свой возврат",
  fbo_service: "FBO-сервис",
  fbs_service: "FBS-сервис",
  return_processing: "Обработка возврата",
};

function pct(value) {
  return `${Math.round(Number(value || 0) * 10000) / 100}%`;
}

function money(value) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function tariffMoney(value) {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatPlainNumber(value) {
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatLogisticsRule(rule = {}) {
  const parts = [];
  const firstLiter = Number(rule.firstLiterAmount || 0);
  const additionalLiter = Number(rule.additionalLiterAmount || 0);
  const base = Number(rule.baseAmount || 0);
  const perLiter = Number(rule.amountPerLiter || 0);
  const perKg = Number(rule.amountPerKg || 0);
  const percent = Number(rule.percent || 0);

  if (firstLiter || additionalLiter) {
    parts.push(`1 л ${tariffMoney(firstLiter)}`);
    parts.push(`след. л ${tariffMoney(additionalLiter)}`);
  } else {
    if (base) parts.push(`база ${tariffMoney(base)}`);
    if (perLiter) parts.push(`${tariffMoney(perLiter)} / л`);
    if (perKg) parts.push(`${tariffMoney(perKg)} / кг`);
  }

  if (rule.priceBand?.label) parts.push(rule.priceBand.label);
  if (percent) parts.push(`${pct(percent)} от цены`);
  if (rule.coefficient !== null && rule.coefficient !== undefined && rule.coefficient !== "") {
    parts.push(`коэф. ${rule.coefficient}${rule.coefficientIncludedInAmount ? " уже в тарифе" : ""}`);
  }

  return parts.join(" · ") || "тариф выбран";
}

function toneByNumber(value) {
  if (Number(value || 0) < 0) return "negative";
  if (Number(value || 0) === 0) return "neutral";
  return "positive";
}

function FieldNumber({ name, label, ...props }) {
  const { min, max } = props;
  const parser = props.parser || parseInputNumber;
  const rules = [];

  if (min !== undefined) {
    rules.push({
      validator: (_, value) => (
        value === undefined || value === null || value === "" || parseNumber(value) >= Number(min)
          ? Promise.resolve()
          : Promise.reject(new Error(`Минимум ${min}`))
      ),
    });
  }

  if (max !== undefined) {
    rules.push({
      validator: (_, value) => (
        value === undefined || value === null || value === "" || parseNumber(value) <= Number(max)
          ? Promise.resolve()
          : Promise.reject(new Error(`Максимум ${max}`))
      ),
    });
  }

  return (
    <Form.Item name={name} label={label} rules={rules}>
      <InputNumber decimalSeparator="," {...props} parser={parser} />
    </Form.Item>
  );
}

function MetricTile({ icon, label, value, tone = "neutral" }) {
  return (
    <div className={`unit-planning-metric unit-planning-metric--${tone}`}>
      <div className="unit-planning-metric__icon">{icon}</div>
      <div className="unit-planning-metric__body">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </div>
  );
}


// publicMode — открытый калькулятор на лендинге: те же поля и вывод, но запросы идут
// в /api/public/unit, сохранённых расчётов нет, а «Сохранить» вызывает onSaveRequest (вход).
// initialValues — значения формы вместо пустых (проценты как в полях, 35 = 35%).
// initialCategorySearch — категория примера: ищется по названию в справочнике тарифа.
// Кластеры Ozon в initialValues можно задать началом названия («Москва»).
// scrollToResult — на ширине до 999 px после расчёта прокрутить к результату.
// autoCalculate — посчитать пример сразу, как только справочник подставил категорию
// (без прокрутки; onCalculated получает auto: true, чтобы не засчитывать цель Метрики).
export default function UnitPlanningCalculator({
  marketplace,
  publicMode = false,
  isDarkOverride,
  onSaveRequest,
  onCalculated,
  initialValues,
  initialCategorySearch = "",
  scrollToResult = false,
  autoCalculate = false,
}) {
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const reduxDark = useSelector((state) => Boolean(state?.ToggleDarkModeReducer?.chekced));
  const isDark = typeof isDarkOverride === "boolean" ? isDarkOverride : reduxDark;
  const apiBase = publicMode ? "/api/public/unit" : "/unit";
  const checkedSessionCompanyId = useSelector((state) => state?.checkedSessionReducer?.checkedSession || "");
  const checkedActionCompanyId = useSelector((state) => state?.userReducerCompanyChecked?.companyID || "");
  const selectedCompanyReloadKey = `${checkedSessionCompanyId || ""}:${checkedActionCompanyId || ""}`;

  const [loadingMeta, setLoadingMeta] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ categories: [], tariffVersions: [], ozonClusters: [], wbWarehouses: [] });
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState(null);
  // У нового пользователя после входа с сайта ещё нет компании: сервер отвечает COMPANY_NOT_SELECTED.
  const [companyMissing, setCompanyMissing] = useState(false);
  const [pendingFromSite, setPendingFromSite] = useState(false);
  const navigate = useNavigate();
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [activeCalculationId, setActiveCalculationId] = useState(null);
  const [savedPage, setSavedPage] = useState(1);
  const [savedLimit, setSavedLimit] = useState(10);
  const [savedTotal, setSavedTotal] = useState(0);
  const [savedSearch, setSavedSearch] = useState("");
  const [savedSearchDraft, setSavedSearchDraft] = useState("");
  const [savedSchemeFilter, setSavedSchemeFilter] = useState("");
  const [savedProfitFilter, setSavedProfitFilter] = useState("");
  const [savedSortField, setSavedSortField] = useState("updatedAt");
  const [savedSortOrder, setSavedSortOrder] = useState("descend");
  const [deletingId, setDeletingId] = useState(null);
  const [categorySearching, setCategorySearching] = useState(false);
  const categorySearchTimerRef = useRef(null);
  const resultPanelRef = useRef(null);
  const [metaLoadCount, setMetaLoadCount] = useState(0);
  const resolvedMetaLoadRef = useRef(0);
  const autoCalculateRef = useRef(null);

  const formDefaults = useMemo(
    () => ({ ...createFormDefaults(marketplace), ...(initialValues || {}) }),
    [initialValues, marketplace]
  );

  const selectedScheme = Form.useWatch("scheme", form);
  const selectedCategoryExternalId = Form.useWatch("categoryExternalId", form);

  const marketplaceName = marketplace === "wb" ? "WB" : "Ozon";
  const title = `${marketplaceName} плановая экономика`;

  const categoryOptions = useMemo(
    () =>
      (meta.categories || []).map((category) => {
        const schemes = category.commissionSchemes || [];
        const hasSchemeCommission = !selectedScheme || schemes.includes(selectedScheme);
        const missingCommission = !hasSchemeCommission;
        const search = normalizeCategoryText([
          category.name,
          category.externalId,
          ...(Array.isArray(category.path) ? category.path : []),
        ].filter(Boolean).join(" "));

        return {
          value: category.externalId,
          label: (
            <span className={`unit-planning-option ${missingCommission ? "unit-planning-option--warning" : ""}`}>
              <span>{category.name}</span>
              <small>
                {category.externalId}
                {missingCommission ? ` · нет комиссии ${String(selectedScheme || "").toUpperCase()}` : ""}
              </small>
            </span>
          ),
          search,
        };
      }),
    [meta.categories, selectedScheme]
  );

  const publishedVersion = useMemo(
    () => (meta.tariffVersions || []).find((item) => item.status === "published"),
    [meta.tariffVersions]
  );

  const ozonClusterOptions = useMemo(
    () =>
      (meta.ozonClusters || []).map((cluster) => {
        const name = cluster.name || cluster.clusterName || cluster.value || "";
        const details = [
          cluster.logisticClusterName,
          cluster.warehouseName,
          cluster.type,
        ].filter(Boolean).join(" В· ");

        return {
          value: name,
          label: details ? `${name} (${details})` : name,
          search: `${name} ${details} ${cluster.id || ""}`.toLowerCase(),
        };
      }),
    [meta.ozonClusters]
  );

  const currentScheme = selectedScheme || (marketplace === "wb" ? "fbw" : "fbo");
  const isWbClickCollect = marketplace === "wb" && currentScheme === "cc";
  const showBuyoutField = !isWbClickCollect;
  const showStorageFields = marketplace === "wb" && currentScheme === "fbw";
  const showOwnDeliveryFields = marketplace === "ozon"
    ? currentScheme === "realfbs"
    : ["dbs", "edbs"].includes(currentScheme);
  const requireOzonClusters = marketplace === "ozon" && currentScheme !== "realfbs";

  const loadMeta = useCallback(async () => {
    const query = new URLSearchParams({ marketplace });
    if (selectedCompanyReloadKey) query.set("_company", selectedCompanyReloadKey);

    setLoadingMeta(true);
    try {
      const { data } = await http.get(`${apiBase}/meta?${query.toString()}`);
      setMeta({
        categories: data?.categories || [],
        tariffVersions: data?.tariffVersions || [],
        ozonClusters: data?.ozonClusters || [],
        wbWarehouses: data?.wbWarehouses || [],
      });
      setMetaLoadCount((count) => count + 1);
      setCompanyMissing(false);
    } catch (error) {
      if (!publicMode && error?.response?.data?.code === "COMPANY_NOT_SELECTED") {
        setCompanyMissing(true);
        return;
      }
      const errorNotice = getErrorNotice(error, "Не удалось загрузить справочники");
      if (errorNotice) setNotice(errorNotice);
    } finally {
      setLoadingMeta(false);
    }
  }, [apiBase, marketplace, publicMode, selectedCompanyReloadKey]);

  const searchCategories = useCallback((value) => {
    const search = String(value || "").trim();

    if (categorySearchTimerRef.current) {
      clearTimeout(categorySearchTimerRef.current);
    }

    if (search.length < 2) {
      setCategorySearching(false);
      return;
    }

    categorySearchTimerRef.current = setTimeout(async () => {
      const query = new URLSearchParams({ marketplace, search });
      if (selectedCompanyReloadKey) query.set("_company", selectedCompanyReloadKey);

      setCategorySearching(true);
      try {
        const { data } = await http.get(`${apiBase}/meta/categories?${query.toString()}`);
        setMeta((current) => ({
          ...current,
          categories: mergeCategoryLists(current.categories, data?.categories || []),
        }));
      } catch (_error) {
        // Поиск не должен перекрывать основную загрузку справочников.
      } finally {
        setCategorySearching(false);
      }
    }, 250);
  }, [apiBase, marketplace, selectedCompanyReloadKey]);

  const loadSavedCalculations = useCallback(async (params = {}) => {
    if (publicMode) return;
    const page = params.page ?? savedPage;
    const limit = params.limit ?? savedLimit;
    const search = params.search ?? savedSearch;
    const scheme = params.scheme ?? savedSchemeFilter;
    const profitState = params.profitState ?? savedProfitFilter;
    const sortField = params.sortField ?? savedSortField;
    const sortOrder = params.sortOrder ?? savedSortOrder;
    const query = new URLSearchParams({
      marketplace,
      page: String(page),
      limit: String(limit),
      sortField,
      sortOrder,
    });

    if (search) query.set("search", search);
    if (scheme) query.set("scheme", scheme);
    if (profitState) query.set("profitState", profitState);
    if (selectedCompanyReloadKey) query.set("_company", selectedCompanyReloadKey);

    setLoadingSaved(true);
    try {
      const { data } = await http.get(`/unit/calculations?${query.toString()}`);
      setSavedCalculations(data?.calculations || []);
      setSavedTotal(data?.pagination?.total || 0);
    } catch (error) {
      // Без компании об этом уже говорит отдельная плашка с кнопкой «Создать компанию».
      if (error?.response?.data?.code === "COMPANY_NOT_SELECTED") return;
      const errorNotice = getErrorNotice(error, "Не удалось загрузить сохраненные расчеты");
      if (errorNotice) setNotice(errorNotice);
    } finally {
      setLoadingSaved(false);
    }
  }, [
    publicMode,
    marketplace,
    savedLimit,
    savedPage,
    savedProfitFilter,
    savedSchemeFilter,
    savedSearch,
    savedSortField,
    savedSortOrder,
    selectedCompanyReloadKey,
  ]);

  useEffect(() => () => {
    if (categorySearchTimerRef.current) {
      clearTimeout(categorySearchTimerRef.current);
    }
  }, []);

  useEffect(() => {
    form.setFieldsValue(formDefaults);
    setResult(null);
    setNotice(null);
    setSavedCalculations([]);
    setSavedTotal(0);
    setMeta({ categories: [], tariffVersions: [], ozonClusters: [], wbWarehouses: [] });
    setActiveCalculationId(null);
    setSavedPage(1);
    setSavedSearch("");
    setSavedSearchDraft("");
    setSavedSchemeFilter("");
    setSavedProfitFilter("");
    setSavedSortField("updatedAt");
    setSavedSortOrder("descend");
    loadMeta();
  }, [form, formDefaults, loadMeta, marketplace, selectedCompanyReloadKey]);

  // Пример из initialValues ссылается на справочник по названиям: подставляем реальные
  // externalId категории и названия кластеров Ozon, когда справочник загрузился.
  useEffect(() => {
    if (!metaLoadCount || resolvedMetaLoadRef.current === metaLoadCount) return;
    resolvedMetaLoadRef.current = metaLoadCount;
    if (!initialValues && !initialCategorySearch) return;

    const clusterNames = (meta.ozonClusters || [])
      .map((cluster) => cluster.name || cluster.clusterName || cluster.value || "")
      .filter(Boolean);
    const clusterPatch = {};
    ["storageClusterId", "deliveryClusterId"].forEach((field) => {
      const value = String(form.getFieldValue(field) || "").trim();
      if (!value || clusterNames.includes(value)) return;
      const wanted = normalizeCategoryText(value);
      clusterPatch[field] = clusterNames.find((name) => normalizeCategoryText(name).startsWith(wanted));
    });
    if (Object.keys(clusterPatch).length) form.setFieldsValue(clusterPatch);

    const search = String(initialCategorySearch || "").trim();
    if (!search || form.getFieldValue("categoryExternalId")) return;

    const wanted = normalizeCategoryText(search);
    const scheme = form.getFieldValue("scheme");
    // Одно название бывает у нескольких категорий справочника (у Ozon «Худи» есть в одежде
    // и в благотворительности), и не у всех есть комиссия для схемы примера: без неё расчёт
    // вернёт ошибку. Берём категорию с комиссией, из них — с самым коротким путём в дереве.
    const hasCommission = (category) => !scheme || (category.commissionSchemes || []).includes(scheme);
    const pathLength = (category) =>
      (Array.isArray(category.path) && category.path.length ? category.path.join(" > ") : String(category.externalId || "")).length;
    const pickCategory = (list = []) => {
      const exact = list.filter((category) => normalizeCategoryText(category.name) === wanted);
      const partial = list.filter((category) => normalizeCategoryText(category.name).includes(wanted));
      const best = (candidates) =>
        candidates.filter(hasCommission).sort((a, b) => pathLength(a) - pathLength(b))[0];
      return best(exact) || best(partial) || exact[0] || partial[0];
    };
    const applyCategory = (category) => {
      if (!category?.externalId) return;
      setMeta((current) => ({
        ...current,
        categories: mergeCategoryLists(current.categories, [category]),
      }));
      form.setFieldValue("categoryExternalId", category.externalId);
      if (autoCalculate) autoCalculateRef.current?.();
    };

    const loaded = pickCategory(meta.categories);
    if (loaded) {
      applyCategory(loaded);
      return;
    }

    const query = new URLSearchParams({ marketplace, search, limit: "20" });
    if (selectedCompanyReloadKey) query.set("_company", selectedCompanyReloadKey);
    http.get(`${apiBase}/meta/categories?${query.toString()}`)
      .then(({ data }) => {
        if (form.getFieldValue("categoryExternalId")) return;
        applyCategory(pickCategory(data?.categories || []));
      })
      .catch(() => {
        // Категорию пример не подставит, посетитель выберет её сам.
      });
  }, [apiBase, autoCalculate, form, initialCategorySearch, initialValues, marketplace, meta.categories, meta.ozonClusters, metaLoadCount, selectedCompanyReloadKey]);

  useEffect(() => {
    loadSavedCalculations();
  }, [loadSavedCalculations]);

  // Расчёт с сайта. Сразу после входа выбранная компания догружается и сбрасывает форму
  // (эффект выше), поэтому расчёт держим в ref и подставляем после каждой загрузки справочника,
  // пока посетитель сам не посчитает или не сохранит.
  const siteCalcRef = useRef(null);
  useEffect(() => {
    // Ждём ответа справочника: до него неизвестно, есть ли у пользователя компания.
    if (publicMode || loadingMeta || (!metaLoadCount && !companyMissing)) return;
    if (!siteCalcRef.current) siteCalcRef.current = readPendingPublicCalc(marketplace)?.input || null;
    const siteInput = siteCalcRef.current;
    if (!siteInput) return;
    form.setFieldsValue(inputToFormValues(siteInput, marketplace));
    // Без компании посчитать и сохранить нельзя: расчёт остаётся ждать её создания.
    if (companyMissing) {
      setPendingFromSite(true);
      return;
    }
    clearPendingPublicCalc();
    setPendingFromSite(false);
    setNotice({ type: "success", text: "Подставили расчёт с сайта. Нажмите «Рассчитать» и «Сохранить»." });
  }, [companyMissing, form, loadingMeta, marketplace, metaLoadCount, publicMode]);

  const buildInput = (values) => formValuesToInput(values, marketplace);
  const getCategoryName = (externalId) =>
    (meta.categories || []).find((category) => category.externalId === externalId)?.name || "";

  // Форма длинная: на узком экране результат оказывается под ней, показываем его сразу.
  const revealResult = () => {
    if (!scrollToResult || typeof window === "undefined" || !window.matchMedia) return;
    if (!window.matchMedia("(max-width: 999px)").matches) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.requestAnimationFrame(() => {
      resultPanelRef.current?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    });
  };

  const calculate = async () => {
    const values = await form.validateFields();
    siteCalcRef.current = null;
    const input = buildInput(values);

    setCalculating(true);
    setNotice(null);
    try {
      const { data } = await http.post(`${apiBase}/calculate`, input);
      setResult(data.result);
      setActiveCalculationId(null);
      onCalculated?.({ input, result: data.result, categoryName: getCategoryName(input.categoryExternalId) });
      revealResult();
    } catch (error) {
      setResult(null);
      const errorNotice = getErrorNotice(error, "Расчет не выполнен");
      if (errorNotice) setNotice(errorNotice);
    } finally {
      setCalculating(false);
    }
  };

  autoCalculateRef.current = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_error) {
      return;
    }
    try {
      const input = buildInput(values);
      const { data } = await http.post(`${apiBase}/calculate`, input);
      setResult(data.result);
      onCalculated?.({ input, result: data.result, categoryName: getCategoryName(input.categoryExternalId), auto: true });
    } catch (_error) {
      // Пример не посчитался — посетитель нажмёт «Рассчитать» сам.
    }
  };

  const solveTargetPrice = async () => {
    const values = await form.validateFields();
    siteCalcRef.current = null;
    const input = buildInput(values);

    setCalculating(true);
    setNotice(null);
    try {
      const { data } = await http.post(`${apiBase}/solve-price`, input);
      form.setFieldValue("price", data.price);
      setResult(data.result);
      setActiveCalculationId(null);
      setNotice({ type: "success", text: "Цена подобрана" });
      onCalculated?.({
        input: { ...input, price: data.price },
        result: data.result,
        categoryName: getCategoryName(input.categoryExternalId),
        solved: true,
      });
      revealResult();
    } catch (error) {
      const errorNotice = getErrorNotice(error, "Не удалось подобрать цену");
      if (errorNotice) setNotice(errorNotice);
    } finally {
      setCalculating(false);
    }
  };

  const saveCalculation = async () => {
    const values = await form.validateFields();
    siteCalcRef.current = null;
    const input = buildInput(values);

    if (publicMode) {
      writePendingPublicCalc(marketplace, input);
      onSaveRequest?.({
        input,
        result,
        categoryName: getCategoryName(input.categoryExternalId),
      });
      return;
    }

    setSaving(true);
    setNotice(null);
    try {
      const payload = {
        title: (meta.categories || []).find((category) => category.externalId === input.categoryExternalId)?.name || "",
        input,
      };
      const { data } = activeCalculationId
        ? await http.put(`/unit/calculations/${activeCalculationId}`, payload)
        : await http.post("/unit/calculations", payload);
      const nextPage = activeCalculationId ? savedPage : 1;

      setResult(data?.calculation?.result || data?.snapshot?.result || null);
      setActiveCalculationId(data?.calculation?._id || null);
      setSavedPage(nextPage);
      await loadSavedCalculations({ page: nextPage });
      setNotice({ type: "success", text: activeCalculationId ? "Расчет обновлен" : "Расчет сохранен" });
    } catch (error) {
      const errorNotice = getErrorNotice(error, "Не удалось сохранить расчет");
      if (errorNotice) setNotice(errorNotice);
    } finally {
      setSaving(false);
    }
  };

  const openSavedCalculation = (item) => {
    const input = item?.input || {};
    const normalizedInput = inputToFormValues(input, marketplace);

    form.setFieldsValue(normalizedInput);
    setResult(item?.result || null);
    setActiveCalculationId(item?._id || null);
    setNotice({ type: "success", text: "Сохраненный расчет открыт" });

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const startNewCalculation = () => {
    form.setFieldsValue(createFormDefaults(marketplace));
    setResult(null);
    setActiveCalculationId(null);
    setNotice(null);
  };

  const deleteSavedCalculation = async (item) => {
    setDeletingId(item?._id || null);
    setNotice(null);
    try {
      await http.delete(`/unit/calculations/${item._id}`);
      const nextPage = savedCalculations.length === 1 && savedPage > 1 ? savedPage - 1 : savedPage;

      if (activeCalculationId === item._id) {
        setActiveCalculationId(null);
        setResult(null);
      }

      setSavedPage(nextPage);
      await loadSavedCalculations({ page: nextPage });
      setNotice({ type: "success", text: "Расчет удален" });
    } catch (error) {
      const errorNotice = getErrorNotice(error, "Не удалось удалить расчет");
      if (errorNotice) setNotice(errorNotice);
    } finally {
      setDeletingId(null);
    }
  };

  const applyAutoBuyout = (categoryExternalId, scheme = currentScheme) => {
    if (marketplace === "wb" && scheme === "cc") {
      form.setFieldValue("buyoutRate", 100);
      return;
    }

    const category = (meta.categories || []).find((item) => item.externalId === categoryExternalId);
    if (category) {
      form.setFieldValue("buyoutRate", getDefaultBuyoutPercent(category));
    }
  };

  const handleFormValuesChange = (changedValues) => {
    if (result) {
      setResult(null);
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, "categoryExternalId")) {
      applyAutoBuyout(changedValues.categoryExternalId);
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, "scheme")) {
      applyAutoBuyout(selectedCategoryExternalId, changedValues.scheme);
      form.setFieldsValue({
        wbWarehouseMode: undefined,
        wbWarehouseName: undefined,
      });
    }

    if (Object.prototype.hasOwnProperty.call(changedValues, "wbWarehouseMode")) {
      if (changedValues.wbWarehouseMode === "average") {
        form.setFieldValue("wbWarehouseName", undefined);
      }
    }
  };

  const breakdownRows = useMemo(
    () =>
      result
        ? Object.entries(result.costs || {})
            .map(([key, value]) => ({
              key,
              name: COST_LABELS[key] || key,
              value,
              isTotal: key === "total",
            }))
        : [],
    [result]
  );
  const appliedLogisticsRules = result?.appliedRules?.logisticsRules || [];
  const ozonLogisticsBreakdown = marketplace === "ozon"
    ? result?.appliedRules?.logisticsBreakdown
    : null;

  const resultMetrics = [
    {
      icon: <CircleDollarSign size={18} />,
      label: "Прибыль",
      value: result ? money(result.profit) : "-",
      tone: result ? toneByNumber(result.profit) : "neutral",
    },
    {
      icon: <Percent size={18} />,
      label: "Маржа",
      value: result ? pct(result.margin) : "-",
      tone: result ? toneByNumber(result.margin) : "neutral",
    },
    {
      icon: <TrendingUp size={18} />,
      label: "Окупаемость",
      value: result ? pct(result.roi) : "-",
      tone: result ? toneByNumber(result.roi) : "neutral",
    },
    {
      icon: <Wallet size={18} />,
      label: "Цена покупателя",
      value: result ? money(result.priceBuyer) : "-",
      tone: "neutral",
    },
  ];

  const applySavedSearch = () => {
    setSavedSearch(savedSearchDraft.trim());
    setSavedPage(1);
  };

  const resetSavedFilters = () => {
    setSavedSearch("");
    setSavedSearchDraft("");
    setSavedSchemeFilter("");
    setSavedProfitFilter("");
    setSavedSortField("updatedAt");
    setSavedSortOrder("descend");
    setSavedPage(1);
  };

  const getSavedSortOrder = (field) => (savedSortField === field ? savedSortOrder : null);

  const handleSavedTableChange = (pagination, _filters, sorter) => {
    const nextSortField = sorter?.order ? (sorter.columnKey || "updatedAt") : "updatedAt";
    const nextSortOrder = sorter?.order || "descend";

    setSavedPage(pagination?.current || 1);
    setSavedLimit(pagination?.pageSize || savedLimit);
    setSavedSortField(nextSortField);
    setSavedSortOrder(nextSortOrder);
  };

  const savedColumns = [
    {
      title: "Расчет",
      dataIndex: "title",
      columnKey: "title",
      sorter: true,
      sortOrder: getSavedSortOrder("title"),
      render: (_, record) => (
        <div className="unit-planning-saved-title">
          <strong>{record.title || record.categoryName || "Без названия"}</strong>
          <span>
            {record.categoryName || "-"} · {record.categoryExternalId}
          </span>
        </div>
      ),
    },
    {
      title: "Схема",
      dataIndex: "scheme",
      columnKey: "scheme",
      width: 92,
      sorter: true,
      sortOrder: getSavedSortOrder("scheme"),
      render: (value) => <Tag>{String(value || "").toUpperCase()}</Tag>,
    },
    {
      title: "Прибыль",
      dataIndex: ["result", "profit"],
      columnKey: "profit",
      width: 130,
      align: "right",
      sorter: true,
      sortOrder: getSavedSortOrder("profit"),
      render: (value) => (
        <Text className={`unit-planning-saved-money unit-planning-saved-money--${toneByNumber(value)}`} strong>
          {money(value)}
        </Text>
      ),
    },
    {
      title: "Маржа",
      dataIndex: ["result", "margin"],
      columnKey: "margin",
      width: 96,
      align: "right",
      sorter: true,
      sortOrder: getSavedSortOrder("margin"),
      render: (value) => pct(value),
    },
    {
      title: "Обновлен",
      dataIndex: "updatedAt",
      columnKey: "updatedAt",
      width: 150,
      sorter: true,
      sortOrder: getSavedSortOrder("updatedAt"),
      render: (value) => formatDate(value),
    },
    {
      title: "Действия",
      key: "actions",
      width: 205,
      render: (_, record) => (
        <Space size={6} wrap>
          <Button
            size="small"
            icon={<Pencil size={14} />}
            onClick={() => openSavedCalculation(record)}
          >
            Открыть
          </Button>
          <Popconfirm
            title="Удалить расчет?"
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteSavedCalculation(record)}
          >
            <Button
              danger
              size="small"
              icon={<Trash2 size={14} />}
              loading={deletingId === record._id}
            >
              Удалить
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tariffStatusText = publishedVersion ? publishedVersion.code : "Тариф не опубликован";
  const tariffStatusTone = publishedVersion ? "ready" : "warning";

  return (
    <div
      className={[
        "unit-planning-page",
        `unit-planning-page--${marketplace}`,
        isDark ? "unit-planning-page--dark" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="unit-planning-hero">
        <div className="unit-planning-hero__main">
          <div className="unit-planning-hero__icon">
            <Calculator size={21} />
          </div>
          <div className="unit-planning-hero__copy">
            <Title level={2}>{title}</Title>
            <div className="unit-planning-hero__meta">
              <Tag className="unit-planning-market-tag">{marketplaceName}</Tag>
              <span className={`unit-planning-status unit-planning-status--${tariffStatusTone}`}>
                <Database size={13} />
                {tariffStatusText}
              </span>
              <span className="unit-planning-status">
                <Package size={13} />
                {meta.categories?.length || 0} категорий
              </span>
            </div>
          </div>
        </div>

      </div>

      {companyMissing && !publicMode && (
        <div className="unit-planning-notice unit-planning-notice--company">
          <AlertCircle size={17} />
          <span>
            {pendingFromSite
              ? "Расчёт с сайта сохранён. Чтобы посчитать его по тарифам сервиса и сохранить, создайте компанию: после этого калькулятор откроется с вашим расчётом."
              : "Чтобы считать и сохранять расчёты, создайте компанию."}
          </span>
          <Button type="primary" onClick={() => navigate("/sellertnewuserpage")}>Создать компанию</Button>
        </div>
      )}

      {!publishedVersion && !loadingMeta && !companyMissing && (
        <div className="unit-planning-notice">
          <AlertCircle size={17} />
          <span>Нет опубликованной версии тарифов. После синхронизации WB или импорта Ozon тарифов расчет начнет брать правила из БД.</span>
        </div>
      )}

      {notice && (
        <div className={`unit-planning-notice unit-planning-notice--${notice.type}`}>
          {notice.type === "success" ? <CheckCircle size={17} /> : <AlertCircle size={17} />}
          <span>{notice.text}</span>
        </div>
      )}

      <div className="unit-planning-metrics-grid">
        {resultMetrics.map((metric) => (
          <MetricTile key={metric.label} {...metric} />
        ))}
      </div>

      <Spin spinning={loadingMeta}>
        <div className="unit-planning-workspace">
          <div className="unit-planning-panel unit-planning-panel--form">
            <Form
              form={form}
              layout="vertical"
              initialValues={formDefaults}
              onValuesChange={handleFormValuesChange}
            >
              <div className="unit-planning-section">
                <div className="unit-planning-section__head">
                  <Package size={16} />
                  <span>Товар</span>
                </div>
                <Row gutter={[12, 2]}>
                  <Col xs={24} md={12}>
                    <Form.Item name="scheme" label="Схема" rules={[{ required: true, message: "Выберите схему" }]}>
                      <Select options={SCHEMES[marketplace]} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item name="asOf" label="Дата расчёта">
                      <Input type="date" />
                    </Form.Item>
                  </Col>
                  <Col xs={24}>
                    <Form.Item name="categoryExternalId" label="Категория" rules={[{ required: true, message: "Выберите категорию" }]}>
                      <Select
                        showSearch
                        optionFilterProp="search"
                        options={categoryOptions}
                        placeholder="Выберите категорию"
                        loading={categorySearching}
                        onSearch={searchCategories}
                        filterOption={(input, option) =>
                          String(option?.search || option?.value || "")
                            .toLowerCase()
                            .includes(normalizeCategoryText(input))
                        }
                        notFoundContent={categorySearching ? <Spin size="small" /> : null}
                        listHeight={screens.md ? 320 : 240}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="price" label={marketplace === "ozon" ? "Ваша цена, ₽" : "Цена, ₽"} min={1} />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="costPrice" label="Себестоимость, ₽" min={0} />
                  </Col>
                  {showBuyoutField && (
                    <Col xs={12} md={8}>
                      <FieldNumber name="buyoutRate" label="Выкуп, %" min={1} max={100} step={1} precision={0} />
                    </Col>
                  )}
                  <Col xs={12} md={8}>
                    <FieldNumber name="volumeLiters" label="Объем, л" min={0} step={0.1} />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="weightKg" label="Вес, кг" min={0} step={0.1} />
                  </Col>
                  {showStorageFields && (
                    <Col xs={12} md={8}>
                      <FieldNumber name="storageDays" label="Дней хранения" min={0} />
                    </Col>
                  )}
                </Row>
              </div>

              <Divider />

              <div className="unit-planning-section">
                <div className="unit-planning-section__head">
                  <Truck size={16} />
                  <span>Маркетплейс</span>
                </div>
                <Row gutter={[12, 2]}>
                  <Col xs={12} md={8}>
                    {marketplace === "ozon" ? (
                      <FieldNumber name="priceBuyer" label="Цена для покупателя, ₽" min={0} />
                    ) : (
                      <FieldNumber name="marketplaceDiscountRate" label="Скидка МП, %" min={0} max={95} step={0.1} precision={2} />
                    )}
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="acquiringRate" label="Эквайринг, %" min={0} max={100} step={0.1} precision={2} />
                  </Col>
                  {showOwnDeliveryFields && (
                    <>
                      <Col xs={12} md={8}>
                        <FieldNumber name="ownDeliveryCost" label="Своя доставка, ₽" min={0} />
                      </Col>
                      <Col xs={12} md={8}>
                        <FieldNumber name="ownReturnCost" label="Свой возврат, ₽" min={0} />
                      </Col>
                    </>
                  )}
                  {requireOzonClusters && (
                    <>
                      <Col xs={12} md={8}>
                        <Form.Item
                          name="storageClusterId"
                          label="Кластер хранения"
                          rules={requireOzonClusters ? [{ required: true, message: "Выберите кластер хранения" }] : []}
                        >
                          <AutoComplete
                            allowClear
                            options={ozonClusterOptions}
                            placeholder={ozonClusterOptions.length ? "Выберите кластер" : "Кластеры не загружены"}
                            filterOption={(input, option) =>
                              String(option?.search || option?.value || "")
                                .toLowerCase()
                                .includes(String(input || "").toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={12} md={8}>
                        <Form.Item
                          name="deliveryClusterId"
                          label="Кластер доставки"
                          rules={requireOzonClusters ? [{ required: true, message: "Выберите кластер доставки" }] : []}
                        >
                          <AutoComplete
                            allowClear
                            options={ozonClusterOptions}
                            placeholder={ozonClusterOptions.length ? "Выберите кластер" : "Кластеры не загружены"}
                            filterOption={(input, option) =>
                              String(option?.search || option?.value || "")
                                .toLowerCase()
                                .includes(String(input || "").toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              </div>

              <Divider />

              <div className="unit-planning-section">
                <div className="unit-planning-section__head">
                  <CircleDollarSign size={16} />
                  <span>Расходы и налоги</span>
                </div>
                <Row gutter={[12, 2]}>
                  <Col xs={12} md={8}>
                    <FieldNumber name="advertisingRate" label="Реклама, %" min={0} max={100} step={0.1} precision={2} />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="processingCost" label="Обработка, ₽" min={0} />
                  </Col>
                  <Col xs={12} md={8}>
                    <Form.Item name="taxSystem" label="Налог">
                      <Select options={TAX_SYSTEMS} />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="usnRate" label="УСН, %" min={0} max={100} step={0.1} precision={2} />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="vatRate" label="НДС, %" min={0} max={100} step={0.1} precision={2} />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="vatInput" label="Входной НДС, ₽" min={0} />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="otherCosts" label="Прочие, ₽" min={0} />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="targetProfit" label="Целевая прибыль, ₽" />
                  </Col>
                  <Col xs={12} md={8}>
                    <FieldNumber name="targetMargin" label="Целевая маржа, %" step={0.1} precision={2} />
                  </Col>
                </Row>
              </div>

              <div className="unit-planning-actions">
                <Button type="primary" icon={<Calculator size={16} />} loading={calculating} onClick={calculate}>
                  Рассчитать
                </Button>
                <Button icon={<Target size={16} />} loading={calculating} onClick={solveTargetPrice}>
                  Подобрать цену
                </Button>
                <Button icon={<Save size={16} />} loading={saving} onClick={saveCalculation}>
                  {activeCalculationId ? "Сохранить изменения" : "Сохранить"}
                </Button>
                {activeCalculationId && (
                  <>
                    <Button icon={<RefreshCw size={16} />} onClick={startNewCalculation}>
                      Новый расчет
                    </Button>
                    <Tag className="unit-planning-editing-tag">Редактируется сохраненный расчет</Tag>
                  </>
                )}
              </div>
            </Form>
          </div>

          <div className="unit-planning-side-stack">
            <div className="unit-planning-panel unit-planning-result-panel" ref={resultPanelRef}>
              {result ? (
                <>
                  <div className={`unit-planning-profit unit-planning-profit--${toneByNumber(result.profit)}`}>
                    <span>Итоговая прибыль</span>
                    <strong>{money(result.profit)}</strong>
                    <small>
                      Маржа {pct(result.margin)} · Окупаемость {pct(result.roi)}
                    </small>
                  </div>

                  <div className="unit-planning-result-tags">
                    <Tag color="blue">База {pct(result.commissionRate)}</Tag>
                    {Number(result.commissionDiscountPp || 0) !== 0 && (
                      <Tag color="purple">Скидка {pct(result.commissionDiscountPp)}</Tag>
                    )}
                    <Tag color="geekblue">Итог {pct(result.effectiveCommissionRate)}</Tag>
                    {result.scheme !== "cc" && (
                      <Tag color="green">Выкуп {pct(result.buyoutRate)}</Tag>
                    )}
                    {marketplace === "ozon" && (
                      <Tag color={Number(result.commissionDiscountPp || 0) > 0 ? "green" : "default"}>
                        {Number(result.commissionDiscountPp || 0) > 0 ? "Кластерная скидка применена" : "Без кластерной скидки"}
                      </Tag>
                    )}
                    {(result.appliedRules?.discountRules || []).map((rule) => (
                      <Tag key={rule.id || rule.code || rule.title} color="purple">
                        {rule.code || rule.title || "Правило скидки"}
                      </Tag>
                    ))}
                    <Tag>{result.tariffVersionCode || tariffStatusText}</Tag>
                  </div>

                  {appliedLogisticsRules.length > 0 && (
                    <div className="unit-planning-applied-rules">
                      <Typography.Text strong>Примененные тарифы</Typography.Text>
                      {appliedLogisticsRules.map((rule) => {
                        const route = [rule.fromClusterName, rule.toClusterName]
                          .filter(Boolean)
                          .join(" → ");
                        const place = route || [rule.warehouseName, rule.regionName].filter(Boolean).join(" · ");
                        return (
                          <div key={rule.id} className="unit-planning-applied-rules__item">
                            <span>
                              {LOGISTICS_KIND_LABELS[rule.kind] || rule.kind}
                              {place ? ` · ${place}` : ""}
                            </span>
                            <small>{formatLogisticsRule(rule)}</small>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {ozonLogisticsBreakdown && (
                    <div className="unit-planning-applied-rules unit-planning-logistics-breakdown">
                      <Typography.Text strong>Разбор логистики Ozon</Typography.Text>
                      <div className="unit-planning-logistics-breakdown__row">
                        <span>Базовая логистика по маршруту</span>
                        <strong>{tariffMoney(ozonLogisticsBreakdown.forwardPerSale)}</strong>
                        <small>тариф {tariffMoney(ozonLogisticsBreakdown.forwardBase)} / выкуп {pct(result.buyoutRate)}</small>
                      </div>
                      <div className="unit-planning-logistics-breakdown__row">
                        <span>Наценка за нелокальность</span>
                        <strong>{tariffMoney(ozonLogisticsBreakdown.nonlocalMarkup)}</strong>
                        <small>{ozonLogisticsBreakdown.nonlocalMarkupIncluded ? "коэффициент уже учтен в тарифе" : "отдельная наценка не передана справочником"}</small>
                      </div>
                      <div className="unit-planning-logistics-breakdown__row">
                        <span>Доставка до места выдачи</span>
                        <strong>{tariffMoney(ozonLogisticsBreakdown.lastMilePerSale)}</strong>
                        <small>тариф {tariffMoney(ozonLogisticsBreakdown.lastMileBase)} / выкуп {pct(result.buyoutRate)}</small>
                      </div>
                      <div className="unit-planning-logistics-breakdown__row">
                        <span>Обратная логистика</span>
                        <strong>{tariffMoney(ozonLogisticsBreakdown.reversePerSale)}</strong>
                        <small>тариф {tariffMoney(ozonLogisticsBreakdown.reverseBase)} × возвраты {pct(ozonLogisticsBreakdown.returnsPerSale)}</small>
                      </div>
                      <div className="unit-planning-logistics-breakdown__row">
                        <span>Попыток доставки на одну продажу</span>
                        <strong>{formatPlainNumber(ozonLogisticsBreakdown.attemptsPerSale)}</strong>
                        <small>1 / выкуп {pct(result.buyoutRate)}</small>
                      </div>
                      <div className="unit-planning-logistics-breakdown__row unit-planning-logistics-breakdown__row--total">
                        <span>Итоговая логистика</span>
                        <strong>{tariffMoney(ozonLogisticsBreakdown.total)}</strong>
                      </div>
                    </div>
                  )}

                  <Table
                    className="unit-planning-cost-table"
                    size="small"
                    pagination={false}
                    dataSource={breakdownRows}
                    rowClassName={(row) => (row.isTotal ? "unit-planning-cost-table__total" : "")}
                    columns={[
                      { title: "Статья", dataIndex: "name" },
                      {
                        title: "Сумма",
                        dataIndex: "value",
                        align: "right",
                        render: (value) => <Text strong>{money(value)}</Text>,
                      },
                    ]}
                  />
                </>
              ) : (
                <div className="unit-planning-empty">
                  <div className="unit-planning-empty__icon">
                    <Calculator size={30} />
                  </div>
                  <strong>Расчет еще не запущен</strong>
                  <span>Панель покажет прибыль, маржу, окупаемость и структуру затрат.</span>
                  <div className="unit-planning-empty__chips">
                    <span>Комиссия</span>
                    <span>Логистика</span>
                    <span>Налоги</span>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {!publicMode && (
        <div className="unit-planning-panel unit-planning-saved-panel">
          <div className="unit-planning-saved-head">
            <div>
              <History size={17} />
              <span>Сохраненные расчеты</span>
              <Tag>{savedTotal}</Tag>
            </div>
            <Space className="unit-planning-saved-controls" size={8} wrap>
              <Input
                allowClear
                className="unit-planning-saved-search-field"
                placeholder="Название или категория"
                prefix={<Search size={15} />}
                value={savedSearchDraft}
                onPressEnter={applySavedSearch}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setSavedSearchDraft(nextValue);
                  if (!nextValue) {
                    setSavedSearch("");
                    setSavedPage(1);
                  }
                }}
              />
              <Select
                allowClear
                className="unit-planning-saved-filter"
                placeholder="Все схемы"
                value={savedSchemeFilter || undefined}
                options={SCHEMES[marketplace]}
                onChange={(value) => {
                  setSavedSchemeFilter(value || "");
                  setSavedPage(1);
                }}
              />
              <Select
                allowClear
                className="unit-planning-saved-filter"
                placeholder="Прибыль"
                value={savedProfitFilter || undefined}
                options={[
                  { value: "positive", label: "В плюсе" },
                  { value: "negative", label: "В минусе" },
                  { value: "zero", label: "Ноль" },
                ]}
                onChange={(value) => {
                  setSavedProfitFilter(value || "");
                  setSavedPage(1);
                }}
              />
              <Button type="primary" onClick={applySavedSearch}>
                Найти
              </Button>
              <Button onClick={resetSavedFilters}>
                Сбросить
              </Button>
              <Button
                className="unit-planning-ghost-button"
                icon={<RefreshCw size={15} />}
                loading={loadingSaved}
                onClick={() => loadSavedCalculations()}
              >
                Обновить
              </Button>
            </Space>
          </div>

          <Table
            className="unit-planning-saved-table"
            rowKey="_id"
            size="middle"
            loading={loadingSaved}
            columns={savedColumns}
            dataSource={savedCalculations}
            scroll={{ x: 860 }}
            rowClassName={(record) => (
              activeCalculationId === record._id ? "unit-planning-saved-table__active" : ""
            )}
            onChange={handleSavedTableChange}
            pagination={{
              current: savedPage,
              pageSize: savedLimit,
              total: savedTotal,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total) => `Всего: ${total}`,
              onChange: (page, pageSize) => {
                setSavedPage(page);
                setSavedLimit(pageSize);
              },
            }}
          />
        </div>
        )}
      </Spin>
    </div>
  );
}
